import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import SPBackend from '../../services/SPBackend';
import { log } from '../../utils/jsUtils';
import { getFeedPollingInterval } from '../../utils/feedPollingUtils';
import { auth } from '../../services/firebase';

// Utility to handle Transaction aborted errors
const isTransactionAborted = (error) => {
  return (
    error?.data?.body === '"Transaction aborted"' || 
    (error?.status === 500 && error?.data?.includes?.('Transaction aborted'))
  );
};

export const feedApi = createApi({
  reducerPath: 'feedApi',
  baseQuery: fetchBaseQuery({ baseUrl: '/' }), // We'll use SPBackend directly
  tagTypes: ['Feed'],
  keepUnusedDataFor: 30, // Keep data for 30 seconds
  // Note: refetchOnMountOrArgChange is set at hook level (MemberHome.js) for fine-grained control
  endpoints: (builder) => ({
    getFeed: builder.query({
      queryFn: async ({ filters, searchText, userId }) => {
        try {
          if (!userId) {
            log('[feedApi] No userId provided for feed fetch');
            return {
              data: {
                requests: [],
                units: [],
                totalRequests: 0
              }
            };
          }

          log(`[feedApi] getFilteredFeed for searchText: [${searchText}]`, filters);

          // Get the current Firebase user for authentication (stable - doesn't cause refetches)
          const currentUser = auth.currentUser;
          if (!currentUser) {
            log('[feedApi] No authenticated Firebase user');
            return {
              data: {
                requests: [],
                units: [],
                totalRequests: 0
              }
            };
          }

          // Add basic retry mechanism
          let response = null;
          let attempts = 0;
          const maxAttempts = 3;
          let lastError = null;

          while (attempts < maxAttempts) {
            try {
              attempts++;

              // Log retry attempts
              if (attempts > 1) {
                log(`[feedApi] Retry attempt ${attempts}/${maxAttempts}`);
              }

              // Use Firebase currentUser for authentication (not from Redux to avoid cache key changes)
              response = await SPBackend.getFilteredFeed(filters, searchText, currentUser);
              
              // Check for transaction aborted error in response
              if (response?.data?.body === '"Transaction aborted"' || 
                  response?.data?.statusCode === 500) {
                
                log('[feedApi] Transaction aborted error detected:', response.data);
                
                // Only retry if we haven't hit max attempts
                if (attempts < maxAttempts) {
                  // Wait a bit before retrying (exponential backoff)
                  const delay = Math.pow(2, attempts) * 1000;
                  log(`[feedApi] Waiting ${delay}ms before retry ${attempts+1}/${maxAttempts}`);
                  await new Promise(resolve => setTimeout(resolve, delay));
                  continue;
                }
                
                // Return error if max retries reached
                return { 
                  error: {
                    status: 500,
                    data: 'Transaction aborted after multiple retries'
                  } 
                };
              }
              
              break; // Success, exit the loop
              
            } catch (retryError) {
              lastError = retryError;
              log(`[feedApi] Error on attempt ${attempts}/${maxAttempts}:`, retryError);
              
              if (attempts < maxAttempts) {
                // Wait before retrying
                const delay = Math.pow(2, attempts) * 1000;
                log(`[feedApi] Waiting ${delay}ms before retry ${attempts+1}/${maxAttempts}`);
                await new Promise(resolve => setTimeout(resolve, delay));
              }
            }
          }
          
          // If all retries failed, return the last error
          if (!response && lastError) {
            return { 
              error: {
                status: lastError.status || 'FETCH_ERROR',
                data: lastError.message || 'Failed to fetch feed data after multiple retries'
              } 
            };
          }
          
          log('[feedApi] Feed response data:', response?.data);
          
          // Destructure with default empty arrays to avoid undefined errors
          const { Requests = [], Units = [], totalRequests = 0 } = response?.data || {};
          log('[feedApi] Requests:', Requests?.length || 0);
          log('[feedApi] Units:', Units?.length || 0);
          log('[feedApi] totalRequests:', totalRequests);
          
          if (Requests?.length > 0) {
            const R = require('ramda');
            const byDate = R.descend(R.path(['Request', 'UpdatedDt']));
            const sortedRequests = R.sort(byDate, Requests);
            
            return {
              data: {
                requests: sortedRequests,
                units: Units || [],
                totalRequests: totalRequests || sortedRequests.length
              }
            };
          }
          
          return {
            data: {
              requests: [],
              units: Units || [],
              totalRequests: totalRequests || 0
            }
          };
        } catch (error) {
          log('[feedApi] error:', error);
          return { 
            error: {
              status: error.status || 'FETCH_ERROR',
              data: error.message || 'Failed to fetch feed data'
            } 
          };
        }
      },
      providesTags: ['Feed'],
      // Use environment-based polling interval (10s for production, 30s for development)
      pollingInterval: getFeedPollingInterval()
    }),
    getFilterOptions: builder.query({
      queryFn: async ({ userId }) => {
        try {
          // Get the current Firebase user for authentication
          const currentUser = auth.currentUser;
          if (!userId || !currentUser) {
            log('[feedApi] No userId or Firebase user for filter options');
            return { data: {
              propertyOptions: [],
              staffOptions: [],
              tenantOptions: [],
              tagOptions: [],
              unreadMessagesOnlyOptions: []
            }};
          }

          const response = await SPBackend.getFilterOptions(currentUser);
          log('[feedApi] getFilterOptions response:', response.data);
          return { data: response.data || {
            propertyOptions: [],
            staffOptions: [],
            tenantOptions: [],
            tagOptions: [],
            unreadMessagesOnlyOptions: []
          }};
        } catch (error) {
          log('[feedApi] error getting filter options:', error);
          return { 
            error: {
              status: error.status || 'FETCH_ERROR',
              data: error.message || 'Failed to fetch filter options'
            } 
          };
        }
      },
      providesTags: ['Feed']
    })
  })
});

export const {
  useGetFeedQuery,
  useGetFilterOptionsQuery
} = feedApi; 